// A single node in the Trie
class TrieNode {
  children: Map<string, TrieNode>;
  isEndOfWord: boolean;
  word?: string;

  constructor() {
    this.children = new Map();
    this.isEndOfWord = false;
  }
}

// The Trie itself
export class Trie {
  private readonly root: TrieNode;

  constructor() {
    this.root = new TrieNode();
  }

  // Normalize for consistent storage/search: lowercase and map underscores to spaces
  private normalize(s: string): string {
    return s.toLowerCase().replace(/_/g, ' ');
  }

  /**
   * Insert a word into the trie.
   * Example: trie.insert("cat")
   */
  insert(word: string): void {
    const normalized = this.normalize(word);
    let node = this.root;

    for (const ch of normalized) {
      if (!node.children.has(ch)) {
        node.children.set(ch, new TrieNode());
      }
      node = node.children.get(ch)!; // "!" because we know it exists now
    }

    node.isEndOfWord = true;
    node.word = word; // store original word for output
  }

  construct(words: string[]): void {
    for (const word of words) {
      this.insert(word);
    }
  }

  /**
   * Check if a full word exists in the trie.
   * Example: trie.contains("cat") -> true/false
   */
  contains(word: string): boolean {
    const normalized = this.normalize(word);
    let node = this.root;

    for (const ch of normalized) {
      const next = node.children.get(ch);
      if (!next) return false;
      node = next;
    }

    return node.isEndOfWord;
  }

  /**
   * Check if there is *any* word that starts with the given prefix.
   * Example: trie.startsWith("ca") -> true if "cat" or "car" etc exist.
   */
  startsWith(prefix: string): boolean {
    const normalized = this.normalize(prefix);
    let node = this.root;

    for (const ch of normalized) {
      const next = node.children.get(ch);
      if (!next) return false;
      node = next;
    }

    return true;
  }

  /**
   * Get autocomplete suggestions for a given prefix.
   * Optionally limit how many results you want.
   */
  autocomplete(prefix: string, limit = Infinity): string[] {
    const results: string[] = [];

    const normalizedPrefix = this.normalize(prefix);

    // Walk down the trie following the normalized prefix
    let node = this.root;
    for (const ch of normalizedPrefix) {
      const next = node.children.get(ch);
      if (!next) return []; // no words with this prefix
      node = next;
    }

    // From this node, DFS to collect stored original words
    const dfs = (currentNode: TrieNode) => {
      if (results.length >= limit) return;

      if (currentNode.isEndOfWord && currentNode.word) {
        results.push(currentNode.word);
      }

      for (const child of currentNode.children.values()) {
        dfs(child);
        if (results.length >= limit) return;
      }
    };

    dfs(node);
    return results;
  }
}
